import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@cwos/ui-admin';
import { insights } from '@cwos/api-client';
import type { insights as InsightsApi } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { REQUIREMENT_CLASS_CHOICES, floorRefusalSentence } from './applicabilityLogic.js';

/**
 * RULING ON WHETHER A REQUIREMENT GOVERNS THIS ORGANIZATION.
 *
 * ===========================================================================
 * WHY THIS SCREEN EXISTS NOW AND DID NOT BEFORE
 * ===========================================================================
 * `insights:applicability` was `grantability: 'reserved'` and granted to no
 * role from 2032000000000 until founder closeout ruling 5 of 2026-09-08, so
 * `POST /api/insights/applicability` answered 403 for every principal in every
 * tenant. `client-bindings-without-callers.json` recorded the deliberate
 * absence of a control in exactly those words: "a button that always 403s is
 * worse than no button". The ruling made the key a real capability and seeded
 * it to `org_admin`, so the button now has somebody to be for.
 *
 * ===========================================================================
 * THE IMPORT IS FROM `@cwos/api-client` AND NOT FROM `insightsClient.js`
 * ===========================================================================
 * Deliberately, and it is the one thing about this file a future edit must not
 * "tidy". `capability-parity-census.test.ts` decides that an app REACHES a
 * route by following `@cwos/api-client` imports made IN THE APP FILE ITSELF
 * (decision 6 of 2026-09-08); a call routed through the local re-export is
 * invisible to the ledger, which is how the agent screens' reach went
 * unmeasured. The `FindingDetail` type comes from the same place for the same
 * reason.
 *
 * ===========================================================================
 * WHAT THIS CONTROL WILL NOT LET SOMEBODY SAY
 * ===========================================================================
 * FOUR REFUSALS ARE THE SERVER'S AND THE SCREEN DOES NOT RE-IMPLEMENT THEM.
 * A form that hid the impossible options would teach a compliance officer that
 * the product has no opinion about them — and the ruling's whole point is that
 * it does, in words a person should read. So `platform_constraint` is offered,
 * `historical` is offered, and the refusal comes back as a SENTENCE.
 *
 *   `platform_safety_control` — a suppressing ruling against Phibian's own
 *   execution envelope. Nobody may make it; no grant helps.
 *
 *   `governing_effective_window` — `not_yet_effective` or `historical` against
 *   a regulatory or contractual requirement. Whether a law is in force is the
 *   source's effective dates, not a person's judgement.
 *
 *   400 — the judgement is malformed: a suppressing ruling with no program or
 *   location, or with no authority cited.
 *
 * THERE IS NO ORGANIZATION-WIDE OPTION FOR A SUPPRESSION, and the scope
 * control offers the program the finding is already about rather than a
 * picker. A finding carries one program and one location; letting somebody
 * rule for a program they were not looking at, from a page about a different
 * one, is the "scope inferred from the thing you happened to have open"
 * mistake the service refuses to make on the server side.
 */

// The namespace, matching how `library` is consumed one directory over: the
// package spells `export * as insights`, so these are members of that
// namespace rather than siblings of it. The VALUE import above is what
// `capability-parity-census.test.ts` follows; a type-only import proves
// nothing about reach.
type FindingDetail = InsightsApi.FindingDetail;
type FindingRequirementClass = InsightsApi.FindingRequirementClass;
type FindingApplicabilityDetermination = InsightsApi.FindingApplicabilityDetermination;

const APPLICABILITY_KEY = 'insights:applicability';

type Disposition = 'applies' | 'does_not_apply' | 'unknown' | 'historical';

export function ApplicabilityPanel({ finding }: { readonly finding: FindingDetail }) {
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const gate = authorityGate(permissions, APPLICABILITY_KEY);
  const mayRule = gateOpen(gate);

  const [requirementClass, setRequirementClass] = useState<FindingRequirementClass>('regulatory');
  const [disposition, setDisposition] = useState<Disposition>('does_not_apply');
  const [basis, setBasis] = useState('');
  const [basisSource, setBasisSource] = useState('');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['insights', 'finding', finding.id] });
    void queryClient.invalidateQueries({ queryKey: ['insights', 'findings'] });
  };

  const record = useMutation({
    mutationFn: () =>
      insights.recordApplicability({
        findingType: finding.findingType,
        scope:
          finding.programId === null
            ? { kind: 'org' }
            : { kind: 'program', programId: finding.programId },
        requirementClass,
        disposition,
        basis,
        basisSource: basisSource.trim() === '' ? null : basisSource,
      }),
    onSuccess: () => {
      setBasis('');
      setBasisSource('');
      invalidate();
    },
  });

  const [endReason, setEndReason] = useState('');
  const end = useMutation({
    mutationFn: (determinationId: string) => insights.endApplicability(determinationId, endReason),
    onSuccess: () => {
      setEndReason('');
      invalidate();
    },
  });

  // A suppressing ruling needs a citation and the server refuses one without,
  // so the button says so BEFORE the round trip. This is not a second
  // implementation of the rule: it is the same rule said early, and the server
  // still refuses if this check is wrong.
  const suppressing = disposition === 'does_not_apply' || disposition === 'historical';
  const incomplete = basis.trim().length < 3 || (suppressing && basisSource.trim().length < 3);

  return (
    <div className="rn-col" data-applicability-panel>
      <GateNotice
        gate={gate}
        what="rule on whether this requirement governs you"
        heading="Ruling on this is not yours to do"
        detail="Recording that a requirement does or does not govern a program needs the applicability permission. It is separate from triage on purpose: dismissing a finding says you handled it and the next sweep undoes it, while this keeps the condition silent until somebody ends it."
        onRetry={() => permissions.refetch()}
      />

      {mayRule ? (
        <div className="rn-col" data-applicability-record>
          <h3 className="rn-card-title">Record a determination</h3>
          <p className="rn-muted">
            This is not a dismissal. It says whether the requirement behind this condition governs{' '}
            {finding.programId === null ? 'this organization' : 'this program'} — and a suppressing
            ruling keeps the condition silent until somebody ends it.
          </p>

          <label>
            What kind of requirement is this?
            <select
              value={requirementClass}
              onChange={(event) =>
                setRequirementClass(event.target.value as FindingRequirementClass)
              }
            >
              {REQUIREMENT_CLASS_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Your ruling
            <select
              value={disposition}
              onChange={(event) => setDisposition(event.target.value as Disposition)}
            >
              <option value="does_not_apply">It does not govern here</option>
              <option value="applies">It does govern here</option>
              <option value="unknown">I looked and could not tell</option>
              <option value="historical">It governed once and was superseded</option>
            </select>
          </label>

          <label>
            Your reasoning
            <textarea
              value={basis}
              onChange={(event) => setBasis(event.target.value)}
              rows={3}
              placeholder="Why this requirement does or does not reach this program."
            />
          </label>

          <label>
            The authority you are relying on
            <input
              value={basisSource}
              onChange={(event) => setBasisSource(event.target.value)}
              placeholder="A statute section, licence class, contract or payer manual."
            />
          </label>
          {suppressing ? (
            <p className="rn-muted">
              A ruling that silences a condition must cite something. Your reasoning alone is not
              enough.
            </p>
          ) : null}

          <Button
            onClick={() => record.mutate()}
            disabled={incomplete || record.isPending}
            data-applicability-submit
          >
            {record.isPending ? 'Recording…' : 'Record this determination'}
          </Button>

          {/* THE REFUSAL, IN WORDS. `floorRefusalSentence` turns the server's
              own `refusal` discriminator into a sentence; a screen that showed
              "Forbidden" here would send somebody looking for a permission
              that does not exist, because no grant makes either of these
              possible. */}
          {record.isError ? (
            <p className="rn-strong" role="alert" data-applicability-refusal>
              {floorRefusalSentence(record.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      {mayRule && finding.applicabilityDeterminations.length > 0 ? (
        <div className="rn-col" data-applicability-end>
          <h3 className="rn-card-title">End a determination</h3>
          <p className="rn-muted">
            Ending is a second recorded act and never an erasure. The original ruling stays, and
            everything it was silencing that nothing else silences comes back.
          </p>
          <label>
            Why it is ending
            <input value={endReason} onChange={(event) => setEndReason(event.target.value)} />
          </label>
          <ul className="rn-list">
            {finding.applicabilityDeterminations.map(
              (determination: FindingApplicabilityDetermination) => (
                <li key={determination.id}>
                  <Button
                    onClick={() => end.mutate(determination.id)}
                    disabled={endReason.trim().length < 3 || end.isPending}
                  >
                    End the {determination.requirementClass.replace(/_/g, ' ')} ruling
                  </Button>
                </li>
              ),
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
